import React from 'react';
import { formatNumber, sumValues } from '../Common/CensusMap';
import { useCensusThemeSummary } from '../Common/hooks/useCensusTheme';
import { CardsSkeleton } from '../Common/CensusSkeletons';


// Cartões em grupos: soma das variáveis do IBGE indicadas em cada tema.
// `of`: chave do cartão usado como total para o percentual (ex.: homens sobre o total de pessoas).
const GROUPS = [
    {
        title: 'População',
        cards: [
            { key: 'people', label: 'Total de pessoas', theme: 'basico', codes: ['v0001'] },
            { key: 'men', label: 'Homens', theme: 'demografia', codes: ['v01007'], of: 'residents' },
            { key: 'women', label: 'Mulheres', theme: 'demografia', codes: ['v01008'], of: 'residents' }
        ]
    },
    {
        title: 'Faixa etária',
        cards: [
            { key: 'children', label: 'Crianças (0–14)', theme: 'demografia', codes: ['v01031', 'v01032', 'v01033'], of: 'residents' },
            { key: 'adults', label: 'Adultos (15–59)', theme: 'demografia', codes: ['v01034', 'v01035', 'v01036', 'v01037', 'v01038', 'v01039'], of: 'residents' },
            { key: 'elderly', label: 'Idosos (60+)', theme: 'demografia', codes: ['v01040', 'v01041'], of: 'residents' }
        ]
    },
    {
        title: 'Domicílios',
        cards: [
            { key: 'households', label: 'Total de domicílios', theme: 'basico', codes: ['v0002'] },
            { key: 'occupied', label: 'Ocupados', theme: 'basico', codes: ['v0007'], of: 'households' },
            { key: 'vacant', label: 'Vagos', theme: 'basico', codes: ['v0009'], of: 'households' },
            { key: 'occasional', label: 'De uso ocasional', theme: 'basico', codes: ['v0008'], of: 'households' }
        ]
    },
    {
        title: 'Povos e Comunidades Tradicionais',
        cards: [
            { key: 'indigenous', label: 'Pessoas indígenas', theme: 'pessoas_indigenas', codes: ['v01690'], of: 'people' },
            { key: 'quilombola', label: 'Pessoas quilombolas', theme: 'pessoas_quilombolas', codes: ['v03196'], of: 'people' }
        ]
    }
];

// Total de moradores da Demografia: denominador das faixas e do sexo (mesma base do arquivo do IBGE).
const RESIDENTS = { key: 'residents', theme: 'demografia', codes: ['v01006'] };

const CARDS = [...GROUPS.flatMap(group => group.cards), RESIDENTS];

const CARD_BY_KEY = Object.fromEntries(CARDS.map(card => [card.key, card]));

const THEMES = [...new Set(CARDS.map(card => card.theme))];

function useThemeTotals(cityId, year, sectorIds) {
    const summaries = {
        basico: useCensusThemeSummary('basico', cityId, year, sectorIds),
        demografia: useCensusThemeSummary('demografia', cityId, year, sectorIds),
        pessoas_indigenas: useCensusThemeSummary('pessoas_indigenas', cityId, year, sectorIds),
        pessoas_quilombolas: useCensusThemeSummary('pessoas_quilombolas', cityId, year, sectorIds)
    };

    const valueByTheme = Object.fromEntries(
        THEMES.map(theme => [
            theme,
            Object.fromEntries((summaries[theme].data?.variables || []).map(({ code, value }) => [code, value]))
        ])
    );

    return {
        isLoading: THEMES.some(theme => summaries[theme].isPending),
        isUpdating: THEMES.some(theme => summaries[theme].isPlaceholderData),
        valueOf: card => {
            const values = card.codes.map(code => valueByTheme[card.theme][code]);
            return values.some(value => value === null || value === undefined) ? null : sumValues(values);
        }
    };
}

/**
 * Cartões do Censo na cidade inteira (sectorIds null) ou só nos setores informados.
 * Usado pelo Panorama Básico e pelo painel do Setor Básico, para os dois mostrarem os mesmos números.
 */
const OverviewCards = ({ cityId, year, sectorIds = null }) => {
    const { isLoading, isUpdating, valueOf } = useThemeTotals(cityId, year, sectorIds);

    const percentOf = card => {
        if (!card.of) return null;
        const value = valueOf(card);
        const total = valueOf(CARD_BY_KEY[card.of]);
        if (value === null || !total) return null;
        return `${((100 * value) / total).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% do total`;
    };

    return (
        <div className={isUpdating ? 'censo-updating' : undefined}>
            {GROUPS.map(group => (
                <section key={group.title} className="censo-panorama-group" aria-label={group.title}>
                    <h6>{group.title}</h6>
                    {isLoading ? (
                        <CardsSkeleton count={group.cards.length} />
                    ) : (
                        <div className="censo-panorama">
                            {group.cards.map(card => (
                                <div key={card.key} className="censo-card">
                                    <span>{card.label}</span>
                                    <strong>{formatNumber(valueOf(card))}</strong>
                                    {percentOf(card) && <small>{percentOf(card)}</small>}
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            ))}
        </div>
    );
};

export default OverviewCards;
